import React, { useState, useRef, useEffect } from "react";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";

type ReelItem = {
  id: number;
  videoSrc: string;
  title?: string;
};

const IG_URL = "https://www.instagram.com/reel/C6WEDjXJvfI/";

const VIDEO_SRCS = [
  "https://tpdb238dk7.ufs.sh/f/Kj0hjNBmPO5eQOc7ZGKv9ECM36AwVUYnk7KPXycj5HtW4hNa", // 1
  "https://tpdb238dk7.ufs.sh/f/Kj0hjNBmPO5eea0We9oTshbzGcBoLUR1POi5CHeNdJ7qMWyZ", // 2
  "https://tpdb238dk7.ufs.sh/f/Kj0hjNBmPO5eNkUITT3lK4Xb2euV91TIqvOB5nGU6FPtkAJE", // 3
  "https://tpdb238dk7.ufs.sh/f/Kj0hjNBmPO5eAoiCdMs9JX4guswUzGFdIMaZ81toyiqCb7mx", // 4
  "https://tpdb238dk7.ufs.sh/f/Kj0hjNBmPO5eD83tk7y2xpUtzWLAZ96JfMm5YNenS7oQHRsq", // 5
  "https://tpdb238dk7.ufs.sh/f/Kj0hjNBmPO5eW477JHcQpiGgVHMOmNwK8sWZYyTAlrbcoLFz", // 7
];

const reels: ReelItem[] = VIDEO_SRCS.map((src, index) => ({
  id: index + 1,
  videoSrc: src,
}));

const ReelsRow: React.FC = () => {
  const [itemsPerView, setItemsPerView] = useState(3);
  const [currentIndex, setCurrentIndex] = useState(0);
  const videoRefs = useRef<{ [key: number]: HTMLVideoElement | null }>({});

  // responsive items per view
  useEffect(() => {
    const updateItemsPerView = () => {
      const width = window.innerWidth;
      if (width < 640) {
        setItemsPerView(1);
      } else if (width < 1024) {
        setItemsPerView(2);
      } else if (width < 1280) {
        setItemsPerView(3);
      } else {
        setItemsPerView(5);
      }
    };

    updateItemsPerView();
    window.addEventListener("resize", updateItemsPerView);
    return () => window.removeEventListener("resize", updateItemsPerView);
  }, []);

  // clamp currentIndex when itemsPerView changes
  useEffect(() => {
    const maxIndex = Math.max(0, reels.length - itemsPerView);
    setCurrentIndex((prevIndex) =>
      prevIndex > maxIndex ? maxIndex : prevIndex
    );
  }, [itemsPerView]);

  const maxIndex = Math.max(0, reels.length - itemsPerView);
  const canPrev = currentIndex > 0;
  const canNext = currentIndex < maxIndex;

  const goPrev = () => {
    if (!canPrev) return;
    setCurrentIndex((i) => Math.max(0, i - 1));
  };

  const goNext = () => {
    if (!canNext) return;
    setCurrentIndex((i) => Math.min(maxIndex, i + 1));
  };

  // this keeps the same movement but makes it consistent
  const translatePercent = -(currentIndex * (100 / itemsPerView));

  const handleMouseEnter = (id: number) => {
    const video = videoRefs.current[id];
    if (video) {
      video
        .play()
        .catch(() => {
          /* ignore autoplay errors */
        });
    }
  };

  const handleMouseLeave = (id: number) => {
    const video = videoRefs.current[id];
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
  };

  return (
    <div className="w-full bg-beige py-16 md:py-20">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-hero font-medium text-[#622935] font-display">
            Our Reels
          </h2>
          <div className="mt-2 h-1 w-24 bg-[#622935] mx-auto rounded-full"></div>
        </div>

        <div className="relative">
          {/* LEFT ARROW */}
          <button
            onClick={goPrev}
            disabled={!canPrev}
            aria-label="Previous"
            className={`absolute -left-3 sm:-left-4 top-1/2 -translate-y-1/2 z-10 inline-flex items-center justify-center w-10 h-10 rounded-full bg-white text-[#622935] shadow-md hover:bg-beige disabled:opacity-40 disabled:cursor-not-allowed transition`}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>

          <div className="overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(${translatePercent}%)` }}
            >
              {reels.map((reel) => (
                <div
                  key={reel.id}
                  className="px-2 md:px-3 shrink-0"
                  style={{ width: `${100 / itemsPerView}%` }}
                >
                  <div
                    className="relative aspect-[9/16] w-full min-h-[400px] md:min-h-[500px] bg-black rounded-2xl overflow-hidden shadow-2xl ring-1 ring-[#622935]/10 hover:ring-[#622935]/40 transition-all hover:scale-105 cursor-pointer"
                    onMouseEnter={() => handleMouseEnter(reel.id)}
                    onMouseLeave={() => handleMouseLeave(reel.id)}
                    onTouchStart={() => handleMouseEnter(reel.id)}
                    onTouchEnd={() => handleMouseLeave(reel.id)}
                  >
                    <video
                      ref={(el) => {
                        videoRefs.current[reel.id] = el;
                      }}
                      src={reel.videoSrc}
                      className="w-full h-full object-cover transition-opacity duration-300"
                      muted
                      loop
                      playsInline
                      preload="metadata"
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent pointer-events-none" />
                    <a
                      href={IG_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Open on Instagram"
                      className="absolute top-3 right-3 z-10 inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/90 text-[#622935] shadow-md hover:bg-white transition"
                    >
                      <ArrowUpRight className="w-5 h-5" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT ARROW */}
          <button
            onClick={goNext}
            disabled={!canNext}
            aria-label="Next"
            className={`absolute -right-3 sm:-right-4 top-1/2 -translate-y-1/2 z-10 inline-flex items-center justify-center w-10 h-10 rounded-full bg-white text-[#622935] shadow-md hover:bg-beige disabled:opacity-40 disabled:cursor-not-allowed transition`}
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReelsRow;
